import React, { useState, useEffect } from 'react';
import Header from '../../Header/Header';
import PatientDetailHeader from './PatientDetailHeader';
import HealthRecord from './HealthRecord';
import NewAppointment from './NewAppointment';
import PreviousAppointment from './PreviousAppointment';
import Prescription from './Prescription';
import Studies from './Studies';
import { useSelector, useDispatch } from 'react-redux';
import patientDetails from '@custom/rtk-apis/patients/patientDetails';
import { AppDispatch, RootState } from '../../store/store';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Loader } from '@ohif/ui';
import { capitalizeWords, customStyle } from '@custom/Utils/utils';
import { useTheme } from "../../../ui/src/contextProviders"
import ImportScan from './ImportScan';
import { differenceInDays, parseISO, isAfter, isBefore } from 'date-fns';
import { _roleType } from '@custom/Utils/constants/appConstants';
import subscribedStudies from '@custom/rtk-apis/subscription/subscribedStudies';

const PatientDetail = () => {
  const hospitalCode = sessionStorage.getItem('hospitalCode') || localStorage.getItem('hospitalCode');
  const location = useLocation()
  const navigate = useNavigate();
  const state = useSelector(state => state);
  const userRole = sessionStorage?.getItem("role")|| localStorage.getItem('role')
  const { uhid } = useParams<{ uhid: string }>();
  const dispatch = useDispatch<AppDispatch>()
  const patientdata = useSelector((state: RootState) => {
    return state?.patientdetailResponce?.result;
  });
  const getSubscriptionStatus = (expiryDate) => {
    if (!expiryDate) return null;

    const today = new Date();
    const expiry = parseISO(expiryDate);
    const daysAfterExpiry = differenceInDays(today, expiry);
    const daysUntilExpiry = differenceInDays(expiry, today);


    const isExpired = isAfter(today, expiry);
    if (daysAfterExpiry >= 7) {
      return {
        status: 'terminated',
        message: 'Your plan has expired. Please renew or upgrade your plan to continue enjoying uninterrupted access.'
      };
    }
    if (isExpired) {
      const daysLeft = Math.max(0, Math.ceil(7 - daysAfterExpiry));
      const deletionDate = new Date(expiry);
      deletionDate.setDate(deletionDate.getDate() + 7); const formattedDate = deletionDate.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).replace(/,/g, '');
      return {
        status: 'expired',
        daysInRetentionPeriod: daysLeft,
        message: `Your plan has expired. You are now in a ${daysLeft} day${(daysLeft === 1 || daysLeft === 0) ? '' : 's'} grace period. Please upgrade your package to continue full access, or download your studies before the grace period ends.`
      };
    }

    if (daysUntilExpiry <= 7) {
      return {
        status: 'warning',
        daysRemaining: daysUntilExpiry,
        message: `Your current plan will expire in ${daysUntilExpiry} day${(daysUntilExpiry === 1 || daysUntilExpiry === 0) ? '' : 's'}. Please make sure to renew or upgrade it before the expiry to avoid any interruption in service.`
      };
    }

    return null;
  };
   const getExtraStorageSubscriptionStatus = (extraStorageArr) => {
    if (!Array.isArray(extraStorageArr) || extraStorageArr.length === 0) return null;

    const allDates = extraStorageArr
      .map(obj => Object.values(obj)[0])
      .filter(dateStr => !!dateStr);

    if (allDates.length === 0) return null;

    // Find the newest (latest) date
    const newestDateStr = allDates.reduce((latest, curr) => {
      return new Date(curr) > new Date(latest) ? curr : latest;
    }, allDates[0]);

    const today = new Date();
    const expiry = parseISO(newestDateStr);
    const daysAfterExpiry = differenceInDays(today, expiry);
    const daysUntilExpiry = differenceInDays(expiry, today);
    const isExpired = isAfter(today, expiry);

    if (daysAfterExpiry >= 7) {
      return {
        status: 'terminated',
        message: 'Your extra storage plan has expired. Please renew or upgrade your plan to continue uninterrupted access to extra storage.'
      };
    }
    if (isExpired) {
      const daysLeft = Math.max(0, Math.ceil(7 - daysAfterExpiry));
      const deletionDate = new Date(expiry);
      deletionDate.setDate(deletionDate.getDate() + 7);
      const formattedDate = deletionDate.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).replace(/,/g, '');
      return {
        status: 'expired',
        daysInRetentionPeriod: daysLeft,
        message: `Your extra storage plan has expired. You are now in a ${daysLeft} day${(daysLeft === 1 || daysLeft === 0) ? '' : 's'} grace period. Please upgrade your package to continue full access, or download your data before the grace period ends.`
      };
    }
    if (daysUntilExpiry <= 7) {
      return {
        status: 'warning',
        daysRemaining: daysUntilExpiry,
        message: `Your extra storage plan will expire in ${daysUntilExpiry} day${(daysUntilExpiry === 1 || daysUntilExpiry === 0) ? '' : 's'}. Please make sure to renew or upgrade it before the expiry to avoid any interruption in service.`
      };
    }
    return null;
  };
  useEffect(() => {
    if (uhid) {
      dispatch(patientDetails({ uhid }));
      dispatch(subscribedStudies({hospitalCode}))
    }
  }, [uhid, dispatch])
  const [activeTab, setActiveTab] = useState(0);
  const allTabs = ['Health Records', 'Previous Appointments', 'New Appointment', 'Prescriptions', 'Studies'];

  const tabs = userRole === _roleType.technician || userRole === _roleType.pathologist || (userRole === _roleType.admin && location?.state?.userRole === _roleType.technician) || (userRole === _roleType.hospitalSuperadmin && location?.state?.userRole === _roleType.technician) || (userRole === _roleType.hospitalSuperadmin && location?.state?.userRole === _roleType.pathologist)
    ? ['Sample Records', 'Import Scans']
    : allTabs;
  const isDarkMode = useTheme();

  const handleIsBackFunctionality = () => {
    navigate(-1);
  }
  return (
    <>
      <div className='page page__full' style={
        isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
      }>
        <Header isBack={true} handleIsBackFunctionality={handleIsBackFunctionality} title={patientdata?.patientDetails
          ? `${capitalizeWords(patientdata?.patientDetails?.firstName)} ${capitalizeWords(patientdata?.patientDetails?.middleName)} ${capitalizeWords(patientdata?.patientDetails?.lastName)}`.trim() : '--'} />
        <div className='page__body'>
          <div className="relative mx-auto flex flex-col">
                      {((userRole === _roleType.hospitalSuperadmin || userRole === _roleType.admin) && state?.hospitalDetailsResponse?.result?.subscriptionDetail?.expiry_date) && (
                        <>
                          {(() => {
                            const subscriptionStatus = getSubscriptionStatus(
                              state.hospitalDetailsResponse.result.subscriptionDetail.expiry_date
                            );

                            if (!subscriptionStatus) return null; return (<div
                              style={{
                                marginBottom: '0.5rem',
                                padding: '0.75rem 1rem',
                                borderRadius: '0.375rem',
                                backgroundColor: subscriptionStatus.status === 'terminated'
                                  ? '#f3f4f6'
                                  : subscriptionStatus.status === 'expired'
                                    ? '#fee2e2'
                                    : '#fef3c7',
                                borderLeft: `4px solid ${subscriptionStatus.status === 'terminated'
                                  ? '#6b7280'
                                  : subscriptionStatus.status === 'expired'
                                    ? '#ef4444'
                                    : '#f59e0b'}`,
                                color: subscriptionStatus.status === 'terminated'
                                  ? '#374151'
                                  : subscriptionStatus.status === 'expired'
                                    ? '#b91c1c'
                                    : '#92400e'
                              }}
                              role="alert"
                            >
                              <div className="flex items-center">
                                <div className="py-1">
                                  <svg
                                    className={`mr-4 h-6 w-6 ${subscriptionStatus.status === 'terminated'
                                      ? 'text-gray-500'
                                      : subscriptionStatus.status === 'expired'
                                        ? 'text-red-500'
                                        : 'text-yellow-500'
                                      }`}
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                </div>
                                <div>
                                  <p style={{ fontWeight: 'bold' }}>{subscriptionStatus.message}</p>
                                  {(subscriptionStatus.status === 'expired' || subscriptionStatus.status === 'warning') && (
                                    <p className="text-sm mt-1">
                                      <Link
                                        to={'/edithospital'}
                                        style={{ fontWeight: 600 }}
                                        className="underline hover:text-red-800"
                                      >
                                        Upgrade your plan
                                      </Link>
                                      {' or contact support for assistance.'}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                            );
                          })()}
                        </>
                      )}
          </div>
          <div className="relative mx-auto flex flex-col">
                      {((userRole === _roleType.hospitalSuperadmin || userRole === _roleType.admin)) && state?.subscribedStudiesSliceResponse?.result?.extraStoragePlan?.extraStorageExpiryDate && (
                        <>
                          {(() => {
                            const subscriptionStatus = getExtraStorageSubscriptionStatus(
                              state?.subscribedStudiesSliceResponse?.result?.extraStoragePlan?.extraStorageExpiryDate);

                            if (!subscriptionStatus) return null; return (<div
                              style={{
                                marginBottom: '0.5rem',
                                padding: '0.75rem 1rem',
                                borderRadius: '0.375rem',
                                backgroundColor: subscriptionStatus.status === 'terminated'
                                  ? '#f3f4f6'
                                  : subscriptionStatus.status === 'expired'
                                    ? '#fee2e2'
                                    : '#fef3c7',
                                borderLeft: `4px solid ${subscriptionStatus.status === 'terminated'
                                  ? '#6b7280'
                                  : subscriptionStatus.status === 'expired'
                                    ? '#ef4444'
                                    : '#f59e0b'}`,
                                color: subscriptionStatus.status === 'terminated'
                                  ? '#374151'
                                  : subscriptionStatus.status === 'expired'
                                    ? '#b91c1c'
                                    : '#92400e'
                              }}
                              role="alert"
                            >
                              <div className="flex items-center">
                                <div className="py-1">
                                  <svg
                                    className={`mr-4 h-6 w-6 ${subscriptionStatus.status === 'terminated'
                                      ? 'text-gray-500'
                                      : subscriptionStatus.status === 'expired'
                                        ? 'text-red-500'
                                        : 'text-yellow-500'
                                      }`}
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                </div>
                                <div>
                                  <p style={{ fontWeight: 'bold' }}>{subscriptionStatus.message}</p>
                                  {(subscriptionStatus.status === 'expired' || subscriptionStatus.status === 'warning') && (
                                    <p className="text-sm mt-1">
                                      <Link
                                        to={'/edithospital'}
                                        style={{ fontWeight: 600 }}
                                        className="underline hover:text-red-800"
                                      >
                                        Upgrade your plan
                                      </Link>
                                      {' or contact support for assistance.'}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                            );
                          })()}
                        </>
                      )}
           </div>
          <div className='details'>
            <div className='form form--innersec'>
              <PatientDetailHeader></PatientDetailHeader>
              <div className="tabsWrap">
                <ul className="tabs">
                  {tabs.map((tab, index) => {
                    // Check role and hide specific tabs for "radiologist"
                    if ((userRole === 'Radiologist' || userRole === 'Clinician' || userRole === 'Nurse' || userRole === 'Resident') && (tab === 'Previous Appointments' || tab === 'New Appointment')) {
                      return null; // Do not render these tabs
                    }

                    return (
                      <li
                        key={index}
                        className={`tabs__link ${activeTab === index ? 'tabs__link--selected' : ''}`}
                        onClick={() => setActiveTab(index)}
                      >
                        {tab}
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="tabsContent mx-4">
                <div className={`tabsContent__content ${activeTab === 0 ? 'tabsContent__content--selected' : ''}`}><HealthRecord ></HealthRecord></div>
                {userRole === _roleType.technician || userRole === _roleType.pathologist || (userRole === _roleType.admin && location?.state?.userRole === _roleType.technician) || (userRole === _roleType.hospitalSuperadmin && location?.state?.userRole === _roleType.technician) || (userRole === _roleType.hospitalSuperadmin && location?.state?.userRole === _roleType.pathologist) ? (
                  <div className={`tabsContent__content ${activeTab === 1 ? 'tabsContent__content--selected' : ''}`}><ImportScan activeTabIndex={activeTab} patientId={patientdata?.patientDetails?._id} /></div>
                ) : (
                  <div className={`tabsContent__content ${activeTab === 1 ? 'tabsContent__content--selected' : ''}`}><PreviousAppointment activeTabIndex={activeTab} uhid={uhid} /></div>
                )}
                <div className={`tabsContent__content ${activeTab === 2 ? 'tabsContent__content--selected' : ''}`}><NewAppointment patient={patientdata?.patientDetails} /></div>
                <div className={`tabsContent__content ${activeTab === 3 ? 'tabsContent__content--selected' : ''}`}><Prescription activeTabIndex={activeTab} /></div>
                <div className={`tabsContent__content ${activeTab === 4 ? 'tabsContent__content--selected' : ''}`}><Studies activeTabIndex={activeTab} /></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {<Loader isLoaderOpen={patientdata?.loading} />}
    </>
  )
}
export default PatientDetail
